import React from "react";
import ModalMobile from "components/global/modalMobile";
import "./styles.css";
import { FaTimes, FaCopy } from "react-icons/fa";
import { Document, pdfjs, Page } from "react-pdf";
import { List } from 'react-virtualized';

// Polyfill & worker setup
if (typeof Promise.withResolvers === 'undefined') {
  window.Promise.withResolvers = function () {
    let resolve, reject;
    const promise = new Promise((res, rej) => { resolve = res; reject = rej; });
    return { promise, resolve, reject };
  };
}
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

export default function PdfViewerModal({ hide, url, medMeta, allData }) {
  const [numPages, setNumPages] = React.useState(null);
  const [page, setPage] = React.useState(1);
  const [isMobile, setIsMobile] = React.useState(false);
  const [isIos, setIsIos] = React.useState(false);
  const [pageWidth, setPageWidth] = React.useState(window.innerWidth);
  const [containerHeight, setContainerHeight] = React.useState(window.innerHeight * 0.9);
  const [progress, setProgress] = React.useState(0);
  const [zoom, setZoom] = React.useState(0.9);
  const [initialZoomApplied, setInitialZoomApplied] = React.useState(false);

  const containerRef = React.useRef(null);
  const wrapperRef   = React.useRef(null);
  const zoomRef      = React.useRef(1);
  const pagesRef     = React.useRef([]);
  const programmaticScroll = React.useRef(false);


  // detect mobile/ios + handle resize
  React.useEffect(() => {
    const ua = navigator.userAgent;
    setIsMobile(/Mobi|Android|iPhone|iPad/.test(ua));
    setIsIos(/iP(hone|od|ad)/.test(ua));
    const handleResize = () => {
      setPageWidth(window.innerWidth);
      setContainerHeight(window.innerHeight * 0.9);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // iOS pinch-to-zoom hack (unchanged)
  React.useEffect(() => {
  if (!isIos || !containerRef.current || !wrapperRef.current) return;

      const el = containerRef.current;
      const wrapper = wrapperRef.current;

      const MAX_ZOOM = 3.5;
      const MIN_ZOOM = 1;

      let startDist = 0;
      let startZoom = 1;
      let lastZoom = zoomRef.current || 1;

      const getDist = ([a, b]) =>
         Math.hypot(b.clientX - a.clientX, b.clientY - a.clientY);

      const onTouchStart = e => {
        if (e.touches.length === 2) {
          startDist = getDist(e.touches);
          startZoom = lastZoom;
          const midX = (e.touches[0].clientX + e.touches[1].clientX) / 2;
          const midY = (e.touches[0].clientY + e.touches[1].clientY) / 2;

          const rect = wrapper.getBoundingClientRect();
          const originX = (midX - rect.left) / startZoom;
          const originY = (midY - rect.top) / startZoom;

          wrapper.style.transformOrigin = `${originX}px ${originY}px`;
        }
      };

      const onTouchMove = e => {
        if (e.touches.length === 2) {
          e.preventDefault();
          const newDist = getDist(e.touches);
          let newZoom = startZoom * (newDist / startDist);
          newZoom = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, newZoom));

          zoomRef.current = newZoom;
          lastZoom = newZoom;
          wrapper.style.transform = `scale(${newZoom})`;
        }
      };

      const onTouchEnd = e => {
        if (e.touches.length === 0) {
          zoomRef.current = lastZoom;
          startDist = 0;
        }
      };

      el.addEventListener('touchstart', onTouchStart, { passive: false });
      el.addEventListener('touchmove', onTouchMove, { passive: false });
      el.addEventListener('touchend', onTouchEnd, { passive: false });

      return () => {
        el.removeEventListener('touchstart', onTouchStart);
        el.removeEventListener('touchmove', onTouchMove);
        el.removeEventListener('touchend', onTouchEnd);
      };
    }, [isIos]);

  const onLoadSuccess  = ({ numPages }) => { setNumPages(numPages); setPage(1); pagesRef.current = new Array(numPages).fill().map(() => React.createRef()); };
  const onLoadProgress = ({ loaded, total }) => setProgress(Math.round((loaded / total) * 100));

  const loadingSection = () => (
    <div className="relative w-screen h-screen">
      <div className="flex flex-col w-full h-full items-center">
        <div className="relative w-36 h-36 mt-16">
          <div className="absolute inset-0 rounded-full bg-gray-200" />
          <div
            className="absolute inset-0 rounded-full"
            style={{ background: `conic-gradient(#2184db ${progress * 3.6}deg, #D1D5DB 0deg)` }}
          />
          <div className="absolute inset-2 rounded-full bg-white flex items-center justify-center">
            <span className="text-3xl font-semibold text-white rounded-full bg-[#2184db] flex items-center justify-center p-5">
              <FaCopy />
            </span>
          </div>
        </div>
        <h1 className="text-xl text-[#2184db] mt-10 font-semibold">
          Loading File {progress}% ...
        </h1>
      </div>
    </div>
  );

  // Renders one PDF page
  const rowRenderer = ({ index, key, style }) => (
    <div key={key} style={{ ...style, paddingBottom: 10 }}>
      <Page
        pageNumber={index + 1}
        renderTextLayer={false}
        renderAnnotationLayer={false}
        width={pageWidth}
        renderMode="svg"
      />
    </div>
  );

  const handleZoom = (delta) => {
    setZoom(z => {
      const next = Math.max(0.5, Math.min(3, +(z + delta).toFixed(2)));
      return next;
    });
  };

  // keep zoomRef in sync with zoom state so other handlers (eg. iOS pinch) can read it
  React.useEffect(() => { zoomRef.current = zoom; }, [zoom]);

  // compute page render width based on zoom and available width
  // make render width responsive: on narrow screens, use most of the viewport; on wide screens allow max
  const isNarrow = pageWidth < 768;
  const baseWidth = isNarrow ? Math.max(300, pageWidth - 80) : Math.max(600, pageWidth - 160);
  const renderWidth = Math.max(300, Math.min(1200, Math.round(baseWidth * zoom)));

  // When the first page's canvas loads on desktop, compute a zoom so the
  // page's rendered height fits within the containerHeight (taking header
  // area into account). Only apply this once to avoid fighting the user.
  const handleFirstPageLoad = (pageRender) => {
    if (initialZoomApplied || isMobile || !pageRender || !containerRef.current) return;
    try {
      // pageRender has a `div` with canvas inside; find the canvas height
  const canvas = pageRender.querySelector('canvas');
  if (!canvas) return;
  // use layout sizes (pre-transform) to compute target scale
  const naturalHeight = canvas.offsetHeight || canvas.getBoundingClientRect().height || canvas.height;
  const naturalWidth = canvas.offsetWidth || canvas.getBoundingClientRect().width || canvas.width;
  // compute scale needed to fit naturalHeight into containerHeight
  // account for container padding (`p-8` -> 32px on each side) and give a small extra gap
  const containerStylePadding = 32; // left/right padding from `p-8` tailwind
  const extraGap = 50; // extra vertical gap so content doesn't touch edges
  const availableHeight = (containerRef.current.clientHeight || containerHeight) - extraGap;
  // account for wrapper scale origin and margins: leave padding
  const targetScale = Math.max(0.5, Math.min(3, (availableHeight - 20) / naturalHeight));
  // compute corresponding scale constrained by baseWidth so width doesn't overflow
  const widthScale = (pageWidth > 0 && naturalWidth > 0) ? Math.min(targetScale, (pageWidth - containerStylePadding) / naturalWidth) : targetScale;
      const finalScale = Math.max(0.5, Math.min(3, widthScale));
      setZoom(+finalScale.toFixed(2));
      setInitialZoomApplied(true);
    } catch (e) {
      // ignore errors and leave zoom as-is
    }
  };

  const scrollToPage = (index) => {
    const el = pagesRef.current[index];
    if (!el || !containerRef.current) return;
    programmaticScroll.current = true;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    // clear flag after animation (approx)
    setTimeout(() => { programmaticScroll.current = false; }, 600);
  };

  // Observe pages in desktop mode to update current page while scrolling
  React.useEffect(() => {
    if (isMobile || !containerRef.current || !(pagesRef.current && pagesRef.current.length)) return;
    const observerOptions = { root: containerRef.current, rootMargin: '0px', threshold: [0.4, 0.6, 0.8] };
    const obs = new IntersectionObserver((entries) => {
      if (programmaticScroll.current) return; // ignore while programmatic scrolling
      const visible = entries
        .filter(e => e.isIntersecting)
        .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
      if (visible) {
        const idx = pagesRef.current.indexOf(visible.target);
        if (idx !== -1) setPage(idx + 1);
      }
    }, observerOptions);

    pagesRef.current.forEach(el => { if (el) obs.observe(el); });
    return () => { obs.disconnect(); };
  }, [isMobile, numPages, pageWidth, zoom]);

  // Smooth scroll to page when setPage called via buttons
  React.useEffect(() => {
    // only scroll programmatically when page changes via controls
    if (!containerRef.current || !pagesRef.current) return;
    if (!programmaticScroll.current) return;
    const el = pagesRef.current[page - 1];
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [page]);

  return (
    <ModalMobile
      width={`${isMobile ? 'w-full' : 'w-19/20'}`}
      centerModal="modal-scroll overflow-y-auto"
      contentClassName="rounded-lg overflow-auto custom-style"
      wrapperClassName="overflow-visible custom-style shadow-none"
    >
      <div className="w-full">
        <div className={`w-full flex ${isMobile ? 'justify-between p-2 bg-black text-white' : 'justify-end'}`}>
          { isMobile ? <div>{allData?.label || medMeta?.media?.title || 'Document'}</div> : ""}
          <button onClick={hide}>
            <FaTimes className="text-2xl text-white" />
          </button>
        </div>

        {isMobile ? (
          // ─── MOBILE: react-pdf + react-virtualized ─────────────────────────────
          <div
            ref={containerRef}
            className="flex justify-center"
            style={{
              background: 'white',
              touchAction: isIos ? 'pan-y' : 'auto',
            }}
          >
            <div
              ref={wrapperRef}
              style={{
                height: containerHeight,
                width: pageWidth,
                transform: 'scale(1)',
                transformOrigin: '0 0',
                overflow: 'hidden'  // let List handle its own scroll
              }}
            >
              <Document
                file={url}
                onLoadSuccess={onLoadSuccess}
                onLoadProgress={onLoadProgress}
                loading={loadingSection()}
              >
                {numPages && (
                  <List
                    height={containerHeight}
                    width={pageWidth}
                    rowCount={numPages}
                    rowHeight={pageWidth * 1.3}   // tweak per your PDF aspect ratio
                    rowRenderer={rowRenderer}
                    overscanRowCount={3}
                  />
                )}
              </Document>
            </div>
          </div>
        ) : (
          // ─── DESKTOP: react-pdf single-page viewer with controls ─────────────
          <div className="pdf-desktop-view w-full flex flex-col items-center">
            <div className="pdf-header w-full flex items-center justify-between px-6 py-4 rounded-t-lg text-white" style={{ background: '#375ec0' }}>
                <div className="flex items-center gap-4">
                <button className="btn-prev" onClick={() => { const next = Math.max(1, page - 1); setPage(next); scrollToPage(next - 1); }} disabled={page <= 1}>
                  ← Previous
                </button>
                <button className="btn-next" onClick={() => { const next = Math.min(numPages || 1, page + 1); setPage(next); scrollToPage(next - 1); }} disabled={!numPages || page >= numPages}>
                  Next →
                </button>
                <span className="page-indicator">Page {page}{numPages ? ` of ${numPages}` : ''}</span>
                <span className="ml-4 font-semibold">{allData?.label || medMeta?.media?.title || 'Document'}</span>
              </div>
              <div className="flex items-center gap-3">
                <button className="btn-zoom" onClick={() => handleZoom(0.1)}>Zoom In</button>
                <span className="zoom-percent">{Math.round(zoom * 100)}%</span>
                <button className="btn-zoom" onClick={() => handleZoom(-0.1)}>Zoom Out</button>
              </div>
            </div>

            <div className="pdf-container w-full flex justify-center items-start bg-gray-100 p-8" style={{ height: 'calc(100vh - 140px)', overflowY: 'auto', overflowX: 'hidden' }} ref={containerRef}>
              <Document
                file={url}
                onLoadSuccess={onLoadSuccess}
                onLoadProgress={onLoadProgress}
                loading={loadingSection()}
              >
                {/* apply zoom via CSS scale on the pages wrapper for desktop so we don't need to re-render canvases */}
                <div style={{ transformOrigin: 'top center', transform: `scale(${zoom})`, width: `${baseWidth}px` }}>
                  {Array.from({ length: numPages || 0 }).map((_, i) => (
                    <div key={i} ref={el => pagesRef.current[i] = el} className="pdf-page-shadow mb-6 flex justify-center" style={{ transformOrigin: 'top center' }}>
                      <div>
                        <Page
                          pageNumber={i + 1}
                          renderTextLayer={false}
                          renderAnnotationLayer={false}
                          width={baseWidth}
                          onLoadSuccess={i === 0 ? (p) => { /* find DOM node for this page */
                            // react-pdf passes a PDFPageProxy, but we need the rendered DOM
                            // so use pagesRef to access the container element and query for canvas
                            const containerEl = pagesRef.current[0];
                            if (containerEl) handleFirstPageLoad(containerEl);
                          } : undefined}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </Document>
            </div>
          </div>
        )}
      </div>
    </ModalMobile>
  );
}
